import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { RouterModule } from '@angular/router';
import { InstitutionsService } from '@app/core/services/institutions.service';
import { PipesModule } from '@app/shared/pipes/pipes.module';
import { Institution } from '@app/shared/types/Institutions.types';
import { merge } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, startWith } from 'rxjs/operators';

@Component({
  selector: 'app-institutions',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    RouterModule,
    MatPaginatorModule,
    MatSortModule,
    PipesModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './institutions.component.html',
  styleUrls: ['./institutions.component.css'],
})
export class InstitutionsComponent implements OnInit {
  institutions = new MatTableDataSource<Institution>([]);
  pageIndex: number = 0;
  pageSize: number = 20;
  length: number = 0;
  isLoading = false;
  sortBy: string = 'name';
  sortDirection: 'asc' | 'desc' = 'asc';

  displayedColumns: string[] = [
    'vendor',
    'institutionCode',
    'institutionName',
    'parentInstitution',
    'institutionType',
    'location',
    'lastTransactionTime',
  ];

  filterNameControl = new FormControl('');
  vendorFilter = new FormControl('')
  filterParentControl = new FormControl('');
  filterTypeControl = new FormControl('');
  filterDateControl = new FormControl<Date | null>(null);

  institutionTypes: string[] = [
    'Institution',
    'Laboratory',
    'Sector',
    'Internal',
  ];
  typeInstitution = [
    { key: 'Institution', value: 'Ustanova' },
    { key: 'Laboratory', value: 'Laboratorija' },
    { key: 'Sector', value: 'Odeljenje' },
    { key: 'Internal', value: 'Interno' },
  ];

  constructor(private institutionsService: InstitutionsService) {}

  ngOnInit(): void {
    this.loadData();

    merge(
      this.vendorFilter.valueChanges,
      this.filterNameControl.valueChanges,
      this.filterParentControl.valueChanges,
      this.filterTypeControl.valueChanges,
      this.filterDateControl.valueChanges
    )
      .pipe(startWith(null), debounceTime(500), distinctUntilChanged())
      .subscribe(() => {
        this.pageIndex = 0;
        this.loadData();
      });
  }

  handleSortChange(sort: Sort): void {
    const sortMap: { [key: string]: string } = {
      vendor: 'vendor',
      institutionName: 'name',
      institutionType: 'type_name',
      location: 'location_name',
      lastTransactionTime: 'received_dt',
      parentInstitution: 'parent_name',
    };

    this.sortBy = sort.active ? (sortMap[sort.active] || sort.active) : 'name';
    this.sortDirection = (sort.direction as 'asc' | 'desc') || 'asc';

    this.pageIndex = 0;
    this.loadData();
  }

  handlePageEvent(e: PageEvent): void {
    this.pageIndex = e.pageIndex;
    this.loadData();
  }

  loadData(): void {
    this.isLoading = true;
  
    this.institutionsService
      .getInstitutionsNew(
        this.pageIndex * this.pageSize,
        this.pageSize,
        this.filterNameControl.value ?? '',
        this.filterParentControl.value ?? '',
        this.vendorFilter.value ?? '',
        this.filterTypeControl.value ?? '',
        this.sortBy,
        this.sortDirection
      )
      .pipe(
        finalize(() => this.isLoading = false)
      )
      .subscribe({
        next: (res) => {
          this.institutions.data = res?.institutions ?? [];
          this.length = res?.totalCount ?? 0;
        },
        error: () => {
          this.institutions.data = [];
          this.length = 0;
        },
    });
  }
}
